/**
 * Avatar Service - API calls for Avatar Intro Videos feature
 * Handles avatar photo management and AI video generation using Veo
 */

import { getStoredTokens, BASE_URL } from '../api/client';

// Video setting categories
export const SETTING_CATEGORIES = [
    { id: 'studio', name: 'Studio/Office', icon: '🎯' },
    { id: 'outdoor', name: 'Outdoor/Lifestyle', icon: '🚶' },
    { id: 'vehicle', name: 'Vehicle', icon: '🚗' },
    { id: 'property', name: 'Property/Real Estate', icon: '🏠' },
    { id: 'creative', name: 'Creative', icon: '🎨' }
];

// Video settings/scenarios for avatar intro
export const VIDEO_SETTINGS = [
    // Studio/Office
    { id: 'studio_direct', name: 'Studio Direct', icon: '🎯', category: 'studio', description: 'Professional studio look-to-camera intro' },
    { id: 'office_desk', name: 'Office Desk', icon: '🖥️', category: 'studio', description: 'Seated at desk, professional office setting' },
    { id: 'office_standing', name: 'Office Standing', icon: '🏢', category: 'studio', description: 'Standing in modern office environment' },

    // Outdoor/Lifestyle
    { id: 'walk_talk_sidewalk', name: 'Walk & Talk (Sidewalk)', icon: '🚶', category: 'outdoor', description: 'Walking down city sidewalk' },
    { id: 'walk_talk_neighborhood', name: 'Walk & Talk (Neighborhood)', icon: '🏘️', category: 'outdoor', description: 'Strolling through residential area' },
    { id: 'front_yard', name: 'Front Yard', icon: '🌳', category: 'outdoor', description: 'Standing in front of a house' },

    // Vehicle
    { id: 'car_parked', name: 'Car (Parked)', icon: '🚗', category: 'vehicle', description: 'In parked car, casual vibe' },
    { id: 'car_driving', name: 'Car (Driving)', icon: '🚙', category: 'vehicle', description: 'In moving car, dynamic feel' },

    // Property/Real Estate
    { id: 'open_house_door', name: 'Open House Door', icon: '🚪', category: 'property', description: 'Standing at home entrance' },
    { id: 'living_room', name: 'Living Room', icon: '🛋️', category: 'property', description: 'In a beautifully staged living room' },
    { id: 'kitchen_island', name: 'Kitchen Island', icon: '🍳', category: 'property', description: 'At a kitchen island counter' },
    { id: 'backyard_patio', name: 'Backyard Patio', icon: '🏡', category: 'property', description: 'On an outdoor patio' },

    // Creative
    { id: 'selfie_style', name: 'Selfie Style', icon: '🤳', category: 'creative', description: 'Casual selfie-style recording' },
    { id: 'drone_approach', name: 'Drone Approach', icon: '📸', category: 'creative', description: 'Cinematic drone shot approaching' },
    { id: 'green_screen', name: 'Green Screen', icon: '🟩', category: 'creative', description: 'Perfect for custom backgrounds' }
];

// Script categories
export const SCRIPT_CATEGORIES = [
    { id: 'listing', name: 'Listing Intros', icon: '🏠' },
    { id: 'open_house', name: 'Open House', icon: '🚪' },
    { id: 'tips', name: 'Tips & Education', icon: '💡' },
    { id: 'market', name: 'Market Updates', icon: '📊' },
    { id: 'personal', name: 'Personal Brand', icon: '⭐' }
];

// Preset scripts for avatar videos
export const AVATAR_SCRIPTS = [
    // Listing Intros
    { id: 'welcome_listing', category: 'listing', text: 'Welcome to today\'s listing', icon: '🏠' },
    { id: 'beautiful_home', category: 'listing', text: 'One beautiful home a day', icon: '✨' },
    { id: 'new_listing', category: 'listing', text: 'Just listed in your area', icon: '📍' },
    { id: 'dream_home', category: 'listing', text: 'Your dream home awaits', icon: '🌟' },
    { id: 'exclusive_listing', category: 'listing', text: 'Exclusive property alert', icon: '🔑' },

    // Open House
    { id: 'open_house_invite', category: 'open_house', text: 'You\'re invited to our open house', icon: '🚪' },
    { id: 'open_house_this_weekend', category: 'open_house', text: 'Open house this weekend', icon: '📅' },
    { id: 'come_see', category: 'open_house', text: 'Come see this stunning property', icon: '👀' },

    // Tips & Education
    { id: 'tip_of_day', category: 'tips', text: 'Here\'s your real estate tip of the day', icon: '💡' },
    { id: 'market_insight', category: 'tips', text: 'Let me share a quick market insight', icon: '📈' },
    { id: 'homebuyer_tip', category: 'tips', text: 'First-time homebuyer tip', icon: '🏠' },
    { id: 'seller_advice', category: 'tips', text: 'Seller success tip', icon: '💰' },

    // Market Updates
    { id: 'market_update', category: 'market', text: 'Quick market update for you', icon: '📊' },
    { id: 'neighborhood_report', category: 'market', text: 'Your neighborhood market report', icon: '🗺️' },
    { id: 'interest_rates', category: 'market', text: 'Interest rate update', icon: '📉' },

    // Personal Brand
    { id: 'hello_intro', category: 'personal', text: 'Hello, I\'m your local real estate expert', icon: '👋' },
    { id: 'thank_you', category: 'personal', text: 'Thank you for your trust', icon: '🙏' },
    { id: 'lets_connect', category: 'personal', text: 'Let\'s connect and find your perfect home', icon: '🤝' },
    { id: 'here_to_help', category: 'personal', text: 'I\'m here to help you every step', icon: '💪' },
    { id: 'happy_client', category: 'personal', text: 'Another happy client story', icon: '😊' },
    { id: 'milestone', category: 'personal', text: 'Celebrating another milestone', icon: '🎉' }
];

// Video status enum
export const VIDEO_STATUS = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed'
};

// Cost per video generation
export const VIDEO_CREDITS_COST = 500;

// Maximum avatars per user
export const MAX_AVATARS = 5;

// Maximum video duration
export const VIDEO_DURATION_SECONDS = 8;

// Maximum custom script length
export const MAX_SCRIPT_LENGTH = 100;

/**
 * Fetch avatar configuration (scripts, settings, limits)
 */
export async function fetchAvatarConfig() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/config`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch avatar configuration');
    }

    return await response.json();
}

/**
 * Fetch preset scripts
 */
export async function fetchScripts() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/scripts`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch scripts');
    }

    return await response.json();
}

/**
 * Fetch video settings
 */
export async function fetchSettings() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/settings`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch settings');
    }

    return await response.json();
}

/**
 * Upload a new avatar photo
 * @param {File} file - Image file to upload
 * @param {string} name - Optional name for the avatar
 */
export async function uploadAvatar(file, name = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('file', file);
    if (name) {
        formData.append('name', name);
    }

    const response = await fetch(`${BASE_URL}/api/avatars`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 400) {
            throw new Error(errorData.detail || 'Invalid file type or size. Please use JPEG, PNG, or WebP under 10MB.');
        }
        if (response.status === 403) {
            throw new Error(errorData.detail || 'Avatar feature requires Ultra or Enterprise subscription');
        }
        if (response.status === 409) {
            throw new Error(errorData.detail || `Maximum ${MAX_AVATARS} avatars allowed. Please delete an existing avatar.`);
        }
        throw new Error(errorData.detail || 'Failed to upload avatar');
    }

    return await response.json();
}

/**
 * List all avatars for the current user
 */
export async function listAvatars() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to list avatars');
    }

    return await response.json();
}

/**
 * Get a specific avatar by ID
 * @param {string} avatarId - Avatar ID
 */
export async function getAvatar(avatarId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/${avatarId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
            throw new Error('Avatar not found');
        }
        throw new Error(errorData.detail || 'Failed to get avatar');
    }

    return await response.json();
}

/**
 * Update an avatar's metadata
 * @param {string} avatarId - Avatar ID
 * @param {Object} data - Update data (name, etc.)
 */
export async function updateAvatar(avatarId, data) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/${avatarId}`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update avatar');
    }

    return await response.json();
}

/**
 * Delete an avatar
 * @param {string} avatarId - Avatar ID
 */
export async function deleteAvatar(avatarId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/${avatarId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete avatar');
    }

    return true;
}

/**
 * Set an avatar as the default
 * @param {string} avatarId - Avatar ID
 */
export async function setDefaultAvatar(avatarId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/${avatarId}/set-default`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to set default avatar');
    }

    return await response.json();
}

/**
 * Generate an avatar intro video
 * @param {string} avatarId - Avatar ID to use
 * @param {string} setting - Video setting (studio_direct, walk_talk, etc.)
 * @param {string|null} scriptId - Preset script ID (or null for custom)
 * @param {string|null} customScript - Custom script text (if not using preset)
 */
export async function generateVideo(avatarId, setting, scriptId = null, customScript = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const payload = {
        avatar_id: avatarId,
        setting: setting
    };

    if (scriptId) {
        payload.script_id = scriptId;
    } else if (customScript) {
        payload.custom_script = customScript;
    } else {
        throw new Error('Either scriptId or customScript is required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/videos/generate`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 402) {
            throw new Error(errorData.detail || `Insufficient credits. Video generation requires ${VIDEO_CREDITS_COST} credits.`);
        }
        if (response.status === 403) {
            throw new Error(errorData.detail || 'Avatar feature requires Ultra or Enterprise subscription');
        }
        if (response.status === 404) {
            throw new Error(errorData.detail || 'Avatar not found');
        }
        throw new Error(errorData.detail || 'Failed to start video generation');
    }

    return await response.json();
}

/**
 * List all video jobs for the current user
 * @param {number} limit - Max videos to return
 * @param {string|null} cursor - Pagination cursor
 */
export async function listVideos(limit = 20, cursor = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) {
        params.append('cursor', cursor);
    }

    const response = await fetch(`${BASE_URL}/api/avatars/videos?${params}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to list videos');
    }

    return await response.json();
}

/**
 * Get video job status
 * @param {string} videoId - Video job ID
 */
export async function getVideoStatus(videoId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/videos/${videoId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
            throw new Error('Video not found');
        }
        throw new Error(errorData.detail || 'Failed to get video status');
    }

    return await response.json();
}

/**
 * Delete a video
 * @param {string} videoId - Video job ID
 */
export async function deleteVideo(videoId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/avatars/videos/${videoId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete video');
    }

    return true;
}

/**
 * Poll for video completion
 * @param {string} videoId - Video job ID
 * @param {function} onProgress - Callback for progress updates
 * @param {number} maxAttempts - Maximum polling attempts (default 60 = ~5 minutes)
 * @param {number} intervalMs - Polling interval in ms (default 5000 = 5 seconds)
 */
export async function pollVideoCompletion(videoId, onProgress = null, maxAttempts = 60, intervalMs = 5000) {
    let attempts = 0;

    while (attempts < maxAttempts) {
        const status = await getVideoStatus(videoId);

        if (onProgress) {
            onProgress(status);
        }

        if (status.status === VIDEO_STATUS.COMPLETED) {
            return status;
        }

        if (status.status === VIDEO_STATUS.FAILED) {
            throw new Error(status.error_message || 'Video generation failed');
        }

        attempts++;
        await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    throw new Error('Video generation timed out. Please check again later.');
}

/**
 * Get a script by ID
 * @param {string} scriptId - Script ID
 */
export function getScriptById(scriptId) {
    return AVATAR_SCRIPTS.find(s => s.id === scriptId) || null;
}

/**
 * Get scripts by category
 * @param {string} categoryId - Category ID
 */
export function getScriptsByCategory(categoryId) {
    return AVATAR_SCRIPTS.filter(s => s.category === categoryId);
}

/**
 * Get a video setting by ID
 * @param {string} settingId - Setting ID
 */
export function getSettingById(settingId) {
    return VIDEO_SETTINGS.find(s => s.id === settingId) || null;
}

/**
 * Get settings by category
 * @param {string} categoryId - Category ID
 */
export function getSettingsByCategory(categoryId) {
    return VIDEO_SETTINGS.filter(s => s.category === categoryId);
}
